import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Trash2, Building2, Loader2, Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import SuperAdminLayout from "@/components/SuperAdminLayout";
import DataCleanupSections from "@/components/DataCleanupSections";

/**
 * Phase B5 — Super Admin Data Cleanup console (/super-admin/data-cleanup).
 *
 * Cross-org wrapper around the shared `<DataCleanupSections />` component
 * plus a typeahead org picker. Adds the destructive "Full org reset"
 * surface that's hidden from org_admins.
 */
export default function SuperAdminDataCleanup() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [filter, setFilter] = useState("");
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && user.role !== "super_admin") return;
    (async () => {
      try {
        const { data } = await api.get("/super-admin/orgs", { params: { limit: 200 } });
        const items = Array.isArray(data) ? data : (data.items || []);
        setOrgs(items);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [user]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter((o) => (o.name || "").toLowerCase().includes(q));
  }, [orgs, filter]);

  const selectedOrg = useMemo(
    () => orgs.find((o) => o.id === selectedOrgId || o._id === selectedOrgId),
    [orgs, selectedOrgId],
  );

  // All hooks declared above the role guard — rules-of-hooks compliant.
  if (user && user.role !== "super_admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <SuperAdminLayout>
      <div className="space-y-6" data-testid="super-admin-data-cleanup-page">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-navy-900 dark:text-white inline-flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-orange-500" /> Data cleanup
            </h1>
            <p className="text-sm text-navy-500 dark:text-navy-300 mt-1">
              Clear test data for any organisation, or wipe an org's entire dataset (preserves users, sites, branding, billing, audit log, KB).
            </p>
          </div>
        </header>

        <section className="bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 rounded-xl p-5">
          <label className="text-xs font-medium text-navy-600 dark:text-navy-200">Choose an organisation</label>
          <div className="mt-2 flex items-center gap-2 bg-navy-50 dark:bg-navy-900 border border-navy-200 dark:border-navy-700 rounded-md px-3 py-2">
            <Search className="w-4 h-4 text-navy-400" />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by name…"
              className="flex-1 bg-transparent outline-none text-sm"
              data-testid="cleanup-org-filter"
            />
          </div>
          <div className="mt-2 max-h-56 overflow-y-auto border border-navy-100 dark:border-navy-700 rounded-md divide-y divide-navy-100 dark:divide-navy-700">
            {loading && (
              <div className="px-3 py-4 text-xs text-navy-500 inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading orgs…
              </div>
            )}
            {!loading && filtered.slice(0, 50).map((o) => {
              const id = o.id || o._id;
              const isSel = id === selectedOrgId;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setSelectedOrgId(id)}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-3 transition-colors ${
                    isSel
                      ? "bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300"
                      : "hover:bg-navy-50 dark:hover:bg-navy-700"
                  }`}
                  data-testid={`cleanup-org-${id}`}
                >
                  <Building2 className="w-4 h-4 text-navy-400 shrink-0" />
                  <span className="flex-1 truncate font-medium">{o.name || "—"}</span>
                  {o.plan && (
                    <span className="text-[10px] uppercase tracking-wider text-navy-400">{o.plan}</span>
                  )}
                </button>
              );
            })}
            {!loading && filtered.length === 0 && (
              <div className="px-3 py-4 text-xs text-navy-500">No orgs match.</div>
            )}
          </div>
          {selectedOrg && (
            <div className="mt-3 text-xs text-navy-600 dark:text-navy-200">
              Selected: <strong className="text-navy-900 dark:text-white">{selectedOrg.name}</strong>
              <span className="ml-2 font-mono text-navy-400 text-[10px]">{selectedOrg.id || selectedOrg._id}</span>
            </div>
          )}
        </section>

        {selectedOrgId ? (
          <DataCleanupSections
            key={selectedOrgId}                       // remount on org switch — prevents stale previews
            targetOrgId={selectedOrgId}
            scopeIsSuperAdmin={true}
            focusSample={false}
          />
        ) : (
          <div className="bg-navy-50 dark:bg-navy-800 border border-dashed border-navy-200 dark:border-navy-700 rounded-xl p-8 text-center text-sm text-navy-500">
            Pick an organisation above to load its cleanup options.
          </div>
        )}
      </div>
    </SuperAdminLayout>
  );
}
